import { useState, PropsWithChildren, ReactNode, useEffect } from 'react';
import ApplicationLogo from '@/Components/ApplicationLogo';
import Dropdown from '@/Components/Dropdown';
import NavLink from '@/Components/NavLink';
import ResponsiveNavLink from '@/Components/ResponsiveNavLink';
import { Link, router, usePage } from '@inertiajs/react';
import { User } from '@/types';
import { useTranslation } from 'react-i18next';
import { Eye, Global, Moon, Sun1 } from 'iconsax-react';
import { useTheme } from './ThemeContext';
import { UserContext } from './UserContext';
import { BellOutlined } from '@ant-design/icons';
import { Badge } from 'antd';

export default function Authenticated({ user, header, children }: PropsWithChildren<{ user: User, header?: ReactNode }>) {
    const [showingNavigationDropdown, setShowingNavigationDropdown] = useState(false);
    const { t, i18n } = useTranslation();
    const { currentRoute }: string | any = usePage().props;
    const { applang }: string | any = usePage().props;
    const { notifications }: number | any = usePage().props;
    const { theme, toggleTheme } = useTheme();

    useEffect(() => {
        if (applang) {
            i18n.changeLanguage(applang)
        }
    }, [applang, i18n]);

    return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 " dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
            <nav className="bg-white border-b border-gray-100 dark:bg-gray-900 dark:text-white dark:border-b-gray-900">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ">
                    <div className="flex justify-between h-16">
                        <div className="flex">
                            <div className="shrink-0 flex items-center">
                                <Link href="/">
                                    <ApplicationLogo className="block h-9 w-auto fill-current text-gray-800" />
                                </Link>
                            </div>

                            <div className="hidden space-x-8 sm:-my-px sm:ms-10 sm:flex">
                                <NavLink href={route(`${user.roles}.dashboard`)} active={route().current('dashboard')}>
                                    {t('topbar.home')}
                                </NavLink>
                            </div>
                            <div className="hidden space-x-8 sm:-my-px sm:ms-10 sm:flex">
                                <NavLink href={route(`${user.roles}.notification.index`, { locale: i18n.language })} active={false}

                                >
                                    <Badge count={notifications} size="small">
                                        <BellOutlined className='text-lg hover:text-custom-dark-blue' />
                                    </Badge>
                                </NavLink>
                            </div>
                        </div>


                        <div className="hidden sm:flex sm:items-center sm:ms-6">
                            <div>
                                <button className='inline-flex items-center px-3 py-2 text-sm font-medium leading-4 text-gray-500 transition duration-150 ease-in-out border border-transparent rounded-md bg-inhert hover:text-gray-700 focus:outline-none' onClick={toggleTheme}>
                                    {theme === 'dark' ?
                                        <Sun1 size="32" color="#ffff" />
                                        :
                                        <Moon size="32" color="#0033a0" />
                                    }

                                </button>
                            </div>

                            <div className="relative ms-3">
                                <Dropdown>
                                    <Dropdown.Trigger>
                                        <span className="inline-flex rounded-md">
                                            <button
                                                type="button"
                                                className="inline-flex items-center px-3 py-2 text-sm font-medium leading-4 text-gray-500 transition duration-150 ease-in-out border border-transparent rounded-md bg-inhert hover:text-gray-700 focus:outline-none"
                                            >
                                                {theme === 'dark' ?
                                                    <Global size="32" color="#ffff" />
                                                    :
                                                    <Global size="32" color="#0033a0" />
                                                }
                                                <span className='mx-2 dark:text-white'>{i18n.language}</span>

                                            </button>
                                        </span>
                                    </Dropdown.Trigger>

                                    <Dropdown.Content>
                                        <Dropdown.Link href={route(currentRoute, { ...route().params, locale: 'en' })}>English</Dropdown.Link>
                                        <Dropdown.Link href={route(currentRoute, { ...route().params, locale: 'ar' })}>عربي</Dropdown.Link>
                                    </Dropdown.Content>
                                </Dropdown>


                            </div>

                            <div className="ms-3 relative">
                                <Dropdown>
                                    <Dropdown.Trigger>
                                        <span className="inline-flex rounded-md">
                                            <button
                                                type="button"
                                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 bg-white hover:text-gray-700 dark:bg-slate-900 focus:outline-none transition ease-in-out duration-150"
                                            >
                                                {user.name}

                                                <svg
                                                    className="ms-2 -me-0.5 h-4 w-4"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            </button>
                                        </span>
                                    </Dropdown.Trigger>

                                    <Dropdown.Content >
                                        <Dropdown.Link className='dark:bg-slate-800' href={route(`${user.roles}.profile.edit`)}>Profile</Dropdown.Link>
                                        <Dropdown.Link href={route('admin.logout')} method="post" as="button">
                                            Log Out
                                        </Dropdown.Link>
                                    </Dropdown.Content>
                                </Dropdown>
                            </div>
                        </div>

                        <div className="-me-2 flex items-center sm:hidden">
                            <button
                                onClick={() => setShowingNavigationDropdown((previousState) => !previousState)}
                                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-500 transition duration-150 ease-in-out"
                            >
                                <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                                    <path
                                        className={!showingNavigationDropdown ? 'inline-flex' : 'hidden'}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M4 6h16M4 12h16M4 18h16"
                                    />
                                    <path
                                        className={showingNavigationDropdown ? 'inline-flex' : 'hidden'}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                <div className={(showingNavigationDropdown ? 'block' : 'hidden') + ' sm:hidden'}>
                    <div className="pt-2 pb-3 space-y-1">
                        <ResponsiveNavLink href={route(`${user.roles}.dashboard`)} active={route().current('dashboard')}>
                            Dashboard
                        </ResponsiveNavLink>
                    </div>

                    <div className="pt-4 pb-1 border-t border-gray-200">
                        <div className="px-4">
                            <div className="font-medium text-base text-gray-800">
                                {user.name}
                            </div>
                            <div className="font-medium text-sm text-gray-500">{user.email}</div>
                        </div>

                        <div className="mt-3 space-y-1">
                            <ResponsiveNavLink href={route(`${user.roles}.profile.edit`)}>Profile</ResponsiveNavLink>
                            <ResponsiveNavLink method="post" href={route('admin.logout')} as="button">
                                Log Out
                            </ResponsiveNavLink>
                        </div>
                    </div>
                </div>
            </nav>

            {header && (
                <header className="bg-white shadow">
                    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">{header}</div>
                </header>
            )}
            <UserContext.Provider value={{ user: user }}>

                <main >{children}</main>
            </UserContext.Provider>
        </div>
    );
}
